/**
 * One day of a trainee's nutrition, as the trainer reads it: the menu they were given and everything
 * they did to it.
 *
 * This replaced a flat list of the foods the trainee ticked. That list could not answer the question
 * the screen exists for — *is he following the plan* — because the server dropped the plan on the way
 * out: unticked rows, swaps, the alternative meal taken, water and supplement ticks were all resolved
 * and then discarded. They are here now, and the old list is a strict subset of this one.
 *
 * ── the caveat, which is not decoration ──
 *
 * A day the trainee touched carries a FROZEN snapshot of the menu as it was that date. A day they
 * never touched has no row at all, so the menu is resolved live — and if the trainer has changed it
 * since, the date is right and the food is not. `planFrozen` is the only thing that distinguishes
 * those two cases, and a trainer reading last month cannot be left to guess which they are looking at.
 *
 * ── and the older trap it inherits ──
 *
 * Adherence is read off `reported`, never off the numbers. A day can carry a whole plan and non-zero
 * totals with the trainee having said nothing, and colouring that as intake credits them for a meal
 * nobody claims they ate. Every surface here follows the same rule the month grid already does.
 */

import Box from "@mui/material/Box";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Card, EmptyState } from "../ui/primitives";
import { v2 } from "../ui/v2Tokens";
import TargetsSummaryBar from "../builder/TargetsSummaryBar";
import DayCompositionCard from "./DayCompositionCard";
import { mealShares } from "./dayComposition";
import { actualMealTotals, dayActualTotals, dayGoal, menuMeals } from "../model/dayLogV2Model";
import { emptyMacros } from "../model/nutritionV2Model";
import type { TraineeDayV2 } from "../../../../services/FoodLogService";
import { LogMealCard } from "./LogMealCard";
import { DayWaterCard } from "./DayWaterCard";
import { DaySupplementsCard } from "./DaySupplementsCard";

export const DayPlanView = ({ day, isFuture }: { day: TraineeDayV2; isFuture: boolean }) => {
  const { plan, log } = day;

  if (!plan) {
    return (
      <>
        <Card sx={{ padding: "13px 16px", marginBottom: "10px" }}>
          <Box sx={{ fontSize: "12px", color: v2.ink3 }}>לא הוקצה תפריט ליום הזה</Box>
        </Card>
        <Card>
          <EmptyState text={isFuture ? "היום הזה עוד לא הגיע." : "אין נתונים ליום הנבחר."} />
        </Card>
      </>
    );
  }

  const meals = menuMeals(plan);
  const totals = dayActualTotals(plan, log);
  const shares = mealShares(
    meals.map((meal) => ({
      uid: meal.uid,
      name: meal.name,
      // the same totals the meal header prints, so the pill and the header cannot disagree
      calories: actualMealTotals(meal, log).calories,
    })),
  );
  const shareByMeal = new Map(shares.map((share) => [share.uid, share.pct]));

  return (
    <>
      <Card sx={{ padding: "13px 16px", marginBottom: "10px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <Box sx={{ fontSize: "11.5px", color: v2.ink3 }}>התפריט שהוקצה ליום זה</Box>
          <Box
            data-testid="assigned-menu"
            sx={{
              fontSize: "12.5px",
              fontWeight: 600,
              color: v2.brandText,
              backgroundColor: v2.brandWash,
              padding: "3px 9px",
              borderRadius: "7px",
            }}
          >
            {plan.name}
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ fontSize: "11px", color: v2.ink3 }}>
            {day.reported ? "המתאמן דיווח על אכילה ביום הזה." : "המתאמן לא דיווח על היום הזה."}
          </Box>
        </Box>

        {!day.planFrozen && (
          <Box
            data-testid="plan-not-frozen"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "7px",
              marginTop: "9px",
              padding: "8px 10px",
              borderRadius: "8px",
              backgroundColor: v2.warnWash,
              color: v2.warn,
              fontSize: "11.5px",
              lineHeight: 1.5,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 15, marginTop: "1px" }} />
            <Box>התפריט מוצג כפי שהוא מוגדר היום — לא צילום של התפריט שהיה באותו יום.</Box>
          </Box>
        )}
      </Card>

      <TargetsSummaryBar totals={totals} targets={dayGoal(plan) || emptyMacros()} context="log" />

      <DayCompositionCard shares={shares} logged={day.reported} />

      <Box sx={{ marginTop: "10px" }}>
        {meals.map((meal) => (
          <LogMealCard key={meal.uid} meal={meal} log={log} sharePct={shareByMeal.get(meal.uid)} />
        ))}
      </Box>

      <DayWaterCard waterMl={log.waterMl} goalMl={plan.waterGoalMl} />
      <DaySupplementsCard supplements={plan.supplements || []} taken={log.supplementsTaken} />

      {!meals.length && (
        <Card>
          <EmptyState text={isFuture ? "היום הזה עוד לא הגיע." : "אין נתונים ליום הנבחר."} />
        </Card>
      )}
    </>
  );
};
